import { resolveApiBaseUrl } from './apiBaseUrl';
import { resolveSubscribeBaseUrl } from './subscribeBaseUrl';

/**
 * Mint a one-time handoff token via POST /auth/handoff/create and return
 * the fully-qualified subscribe-app URL with the token in the URL **fragment**
 * (`#_h=`). The subscribe app consumes `_h` to bootstrap a session; without
 * it the user lands on the subscribe app's "unauthorized" screen.
 *
 * Security review M5: the token redeems to a full session (incl. the refresh
 * token), so it must ride in the fragment, not the query string — a fragment is
 * never sent to the server and so never lands in CloudFront/S3 access logs,
 * unlike `?_h=` which was logged verbatim during its (short) pre-redeem window.
 * The non-sensitive `promo` code stays in the query.
 *
 * Throws when the local session tokens are missing or when the handoff
 * endpoint rejects the request.
 */
export async function createSubscribeHandoffUrl(
  opts?: { promoCode?: string },
): Promise<string> {
  // Resolve per call (not at module load) so the per-color logic picks
  // up the current hostname — tests rewrite window.location between
  // cases, and prod browsers don't navigate to a different color
  // mid-session anyway.
  const apiBaseUrl = resolveApiBaseUrl(
    window.location.hostname,
    (import.meta as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL,
  );
  const subscribeBase = resolveSubscribeBaseUrl(
    window.location.hostname,
    (import.meta as { env?: { VITE_SUBSCRIBE_BASE?: string } }).env?.VITE_SUBSCRIBE_BASE,
  );

  const accessToken =
    sessionStorage.getItem('accessToken') ?? sessionStorage.getItem('idToken');
  const idToken = sessionStorage.getItem('idToken') ?? '';
  const refreshToken = localStorage.getItem('refreshToken');
  if (!accessToken || !refreshToken) {
    throw new Error('Please sign in first.');
  }
  const res = await fetch(`${apiBaseUrl}/auth/handoff/create`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
      'X-Id-Token': idToken,
      'X-Client-Platform': 'web',
    },
    body: JSON.stringify({ refreshToken }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`);
  }
  const { token } = (await res.json()) as { token: string };
  // Non-sensitive promo code stays in the query; the session token rides in the
  // fragment (query first, then `#`, per URL grammar).
  const query = opts?.promoCode ? `?promo=${encodeURIComponent(opts.promoCode)}` : '';
  return `${subscribeBase}/${query}#_h=${encodeURIComponent(token)}`;
}
